import * as ents from '@attain-apps/entities'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import _isEqual from 'lodash-es/isEqual'

import type { TypeParams } from './type-params'

/**
 * Used to retrieve lists or counts of entities
 */
export interface QueryRequest<TTypeParams extends TypeParams = TypeParams> {
  filter?: QueryRequest.Filter<TTypeParams>
  pagination?: QueryRequest.Pagination
  sort?: QueryRequest.Sort
}
export namespace QueryRequest {
  export const isQueryRequest = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<QueryRequest<TypeParams>>({
      filter: TypeChecker.Factory.optional(Filter.isFilter),
      pagination: TypeChecker.Factory.optional(Pagination.isPagination),
      sort: TypeChecker.Factory.optional(Sort.isSort),
    }),
  ).usingAlias('ec.QueryRequest')
  export const assertIsQueryRequest: TypeAsserter<QueryRequest<TypeParams>> =
    TypeAsserter.Factory.from(isQueryRequest)

  /**
   * Sets the default values for a {@link QueryRequest}
   */
  export type WithDefaults<TTypeParams extends TypeParams> = Required.Semi<
    QueryRequest<TTypeParams>,
    'filter' | 'pagination'
  >
  export namespace WithDefaults {
    export function from<TTypeParams extends TypeParams = TypeParams>(
      request: QueryRequest<TTypeParams> | undefined = {},
    ): WithDefaults<TTypeParams> {
      return {
        ...request,
        filter: request.filter ?? {},
        pagination: request.pagination ?? {},
      }
    }
  }

  /**
   * If `true`, the request is not necessarily going to retrieve all entities.
   * If `false`, the request should always retrieve all entities
   */
  export function isLimited(
    request: QueryRequest<TypeParams> | undefined = {},
  ): boolean {
    const { filter, pagination } = WithDefaults.from(request)
    // The request is limited if it has a non-empty filter or it is paginated
    return (
      !Filter.isEqual(filter, {}) || Pagination.Helpers.isPaginated(pagination)
    )
  }

  /**
   * Determines if two query requests are effectively equivalent, taking into
   * account default values
   */
  export function isEqual(
    a: QueryRequest<TypeParams> | undefined = {},
    b: QueryRequest<TypeParams> | undefined = {},
  ): boolean {
    const aDef = WithDefaults.from(a)
    const bDef = WithDefaults.from(b)
    return (
      Filter.isEqual(aDef.filter, bDef.filter) &&
      Pagination.Helpers.isEqual(aDef.pagination, bDef.pagination) &&
      Sort.Helpers.isEqual(aDef.sort, bDef.sort)
    )
  }

  /**
   * Determines if two query requests are effectively equivalent, taking into
   * account default values, but ignoring pagination
   */
  export function isEqualWithoutPagination(
    a: QueryRequest<TypeParams> | undefined = {},
    b: QueryRequest<TypeParams> | undefined = {},
  ): boolean {
    return isEqual(
      { ...a, pagination: undefined },
      { ...b, pagination: undefined },
    )
  }

  /**
   * Returns `true` if all the data retrieved by `b` is already going to be
   * retrieved by `a`
   */
  export function isSupersededBy(
    a: QueryRequest<TypeParams> | undefined = {},
    b: QueryRequest<TypeParams> | undefined = {},
  ): boolean {
    const aDef = WithDefaults.from(a)
    const bDef = WithDefaults.from(b)
    if (!isLimited(aDef)) {
      // If `a` will retrieve everything, then it supersedes whatever `b`
      // retrieves
      return true
    }
    if (!Filter.isEqual(aDef.filter, bDef.filter)) {
      // Otherwise, if `a` and `b` have different filters, we cannot determine
      // if they will retrieve the same entities, so we cannot say `a`
      // supersedes `b`
      return false
    }
    if (!Pagination.Helpers.isPaginated(aDef.pagination)) {
      // Otherwise, if `a` retrieves all entities for the given filter, since
      // `a` and `b` have the same filter, `a` supersedes `b`
      return true
    }
    if (!Pagination.Helpers.isPaginated(bDef.pagination)) {
      // Otherwise, if `b` retrieves all entities for the given filter, since
      // `a` does not, `a` does not supersede `b`
      return false
    }
    if (!Sort.Helpers.isEqual(aDef.sort, bDef.sort)) {
      // Otherwise, if `a` and `b` have different sorting params, since both
      // have pagination params, we cannot determine if they will retrieve the
      // same entities, so we cannot say `a` supersedes `b`
      return false
    }
    // Otherwise, since `a` and `b` have the same filter and sorting params, it
    // just comes down to if the pagination params for `a` supersede those of
    // `b`
    return Pagination.Helpers.isSupersededBy(aDef.pagination, bDef.pagination)
  }

  /**
   * Returns `true` if some of the data retrieved with `b` will also be retrieved
   * with `a`
   */
  export function overlaps(
    a: QueryRequest<TypeParams> | undefined = {},
    b: QueryRequest<TypeParams> | undefined = {},
  ): boolean {
    const aDef = WithDefaults.from(a)
    const bDef = WithDefaults.from(b)
    return (
      !Pagination.Helpers.isPaginated(aDef.pagination) ||
      !Pagination.Helpers.isPaginated(bDef.pagination) ||
      (isEqualWithoutPagination(aDef, bDef) &&
        Pagination.Helpers.overlaps(aDef.pagination, bDef.pagination))
    )
  }

  /* -------------------------------------------------------------------------- */
  /*                                 Components                                 */
  /* -------------------------------------------------------------------------- */

  export type Pagination = ents.core.common.Request.For.Pagination.ByPosition
  export namespace Pagination {
    export const isPagination: TypeChecker<Pagination> =
      ents.core.common.Request.For.Pagination.ByPosition.isByPosition.usingAlias(
        'ec.QueryRequest.Pagination',
      )

    export import Helpers = ents.core.common.Request.For.Pagination.ByPosition

    export type WithDefaults = Helpers.WithDefaults
  }
  export type Sort =
    ents.core.common.Request.For.Sort<ents.core.common.Attribute.Id>
  export namespace Sort {
    export const isSort: TypeChecker<Sort> =
      ents.core.common.Request.For.Sort.getTypeChecker(
        TypeChecker.isString,
      ).usingAlias('ec.QueryRequest.Sort')

    export import Helpers = ents.core.common.Request.For.Sort

    export type WithDefaults =
      Helpers.WithDefaults<ents.core.common.Attribute.Id>
  }

  export interface Filter<TTypeParams extends TypeParams = TypeParams> {
    searchTerm?: string
    additionalFilters?: TTypeParams['filters']
  }
  export namespace Filter {
    export const isFilter = TypeChecker.Factory.object({
      searchTerm: TypeChecker.Factory.optional(TypeChecker.isString),
    }).usingAlias('ec.Filter')

    /**
     * Determines if two filter requests are effectively equivalent, taking into
     * account default values
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function isEqual(
      a: Filter<TypeParams> | undefined = {},
      b: Filter<TypeParams> | undefined = {},
    ): boolean {
      return (
        a.searchTerm === b.searchTerm &&
        // TODO maybe come up with a way for entity collections to pass their own equality function for additional filters
        _isEqual(a.additionalFilters, b.additionalFilters)
      )
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Scopes                                   */
  /* -------------------------------------------------------------------------- */
  /**
   * Used in query-request plugin for router state.
   * Each scope is containing query params that are currently applied/edited.
   * Examples:
   * Editing scope: { filters: { additionalFilters: { someProp: 'b' } } }
   * Applied scope: { pagination: { skip: 0, quantity: 20 }, filters: { additionalFilters: { someProp: 'a' }, searchTerm: 'test' } }
   * query-request params that are applied to entities are the ones from Applied scope
   * When action is dispatched to apply Editing scope, after router state is updated:
   * Editing scope: undefined
   * Applied scope: { pagination: { skip: 0, quantity: 20 }, filters: { additionalFilters: { someProp: 'b' }, searchTerm: 'test' } }
   */
  export enum Scope {
    // Editing scope of router state that holds query params that are edited but not applied yet
    Editing = 'editing',
    // Applied scope of router state that holds query params that are applied
    Applied = 'applied',
  }
  // Needs to be before namespace is defined
  const SCOPES: ReadonlySet<Scope> = new Set(Object.values(Scope) as Scope[])
  export namespace Scope {
    export const ALL: readonly Scope[] = Array.from(SCOPES)
    export const isScope = TypeChecker.Factory.oneOf(SCOPES)
    export const assertIsScope: TypeAsserter<Scope> =
      TypeAsserter.Factory.from(isScope)

    /**
     * Used as QueryParams type in query-request plugin.
     * Contains query params for each scope (Applied and Editing)
     */
    export type Lookup<TTypeParams extends TypeParams> = Partial<
      Record<Scope, QueryRequest<TTypeParams>>
    >
  }
}
